import { JSONPath } from 'jsonpath-plus';

import { EXPRESSION_TYPES, StepDefinitionId, Temporal } from '@willow/types-iso';

import { FunctionContext } from '../FunctionContext';
import { FunctionLibrary, getFunctionSignature, workflowFunctionFrom } from '../FunctionDefinition';

const workflowLaunchDateTime = workflowFunctionFrom(
  'workflowLaunchTime',
  [] as const,
  EXPRESSION_TYPES.zonedDateTime,
  (ctx) => ctx.workflowLaunchTime,
  'Returns the ZonedDateTime when the workflow was launched.',
);

const workflowLaunchPlainDate = workflowFunctionFrom(
  'workflowLaunchDate',
  [] as const,
  EXPRESSION_TYPES.plainDate,
  (ctx) => ctx.workflowLaunchTime.toPlainDate(),
  'Date-only portion of the workflow launch time.',
);

const stepStatus = workflowFunctionFrom(
  'stepStatus',
  [{ name: 'stepDefinitionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.string,
  (ctx, stepId) => {
    const step = ctx.getStepByDefinitionId(stepId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${stepId} not found`);
    }
    return step.status;
  },
  'Returns the current status string of the specified step.',
);

const stepStatusLastUpdated = workflowFunctionFrom(
  'stepStatusLastUpdated',
  [{ name: 'stepDefinitionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.zonedDateTime,
  (ctx, stepId) => {
    const step = ctx.getStepByDefinitionId(stepId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${stepId} not found`);
    }
    return step.statusLastUpdated;
  },
  'Timestamp of the last status change for the specified step.',
);

const formIsSubmitted = workflowFunctionFrom(
  'formIsSubmitted',
  [{ name: 'stepDefinitionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, formId) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    return step.formSubmissionForLastActivation != null;
  },
  'Boolean indicating whether the specified form step has a submission.',
);

const plainDateFromForm = workflowFunctionFrom(
  'plainDateFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.plainDate,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    // TODO(noImplicitAny)
    return Temporal.PlainDate.from((value as any)[0]);
  },
  'Extracts a PlainDate value from a submitted form via JSONPath.',
);

const stringFromForm = workflowFunctionFrom(
  'stringFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.string,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value: unknown[] | null = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    const onlyValue = value[0];
    if (typeof onlyValue !== 'string') {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${onlyValue}, expected a string`);
    }

    return onlyValue;
  },
  'Extracts a string value from a submitted form via JSONPath.',
);

const numberFromForm = workflowFunctionFrom(
  'numberFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.number,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value: unknown[] | null = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    const onlyValue = value[0];
    if (typeof onlyValue !== 'number' || Number.isNaN(onlyValue)) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${onlyValue}, expected a number`);
    }

    return onlyValue;
  },
  'Extracts a number value from a submitted form via JSONPath.',
);

const booleanFromForm = workflowFunctionFrom(
  'booleanFromForm',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value: unknown[] | null = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    if (value == null || value.length !== 1) {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${value?.length ?? 0} values, expected 1`);
    }

    const onlyValue = value[0];
    if (typeof onlyValue !== 'boolean') {
      throw new Error(`JSONPath ${formFieldJsonPath} resolved to ${onlyValue}, expected a boolean`);
    }

    return onlyValue;
  },
  'Extracts a boolean value from a submitted form via JSONPath.',
);

const preallocatedEntityRef = workflowFunctionFrom(
  'preallocatedEntityRef',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'key', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.string,
  (ctx, stepDefinitionId, key) => {
    const step = ctx.getStepByDefinitionId(stepDefinitionId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${stepDefinitionId} not found`);
    }
    const id = step.preallocatedEntityRefs[key];
    if (id == null) {
      throw new Error(
        `Step ${stepDefinitionId} has no preallocated entity ref for key "${key}" (allocations happen on first activation; check that the step has activated and that the key is declared on its definition).`,
      );
    }
    return id;
  },
  "Returns the branded ID the engine pre-allocated for the named key on the given step. Allocations happen on the step's first activation.",
);

const formValueIsDefined = workflowFunctionFrom(
  'formValueIsDefined',
  [
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
    { name: 'jsonPath', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, formId, formFieldJsonPath) => {
    const step = ctx.getStepByDefinitionId(formId as StepDefinitionId);
    if (step == null) {
      throw new Error(`Step with definitionId ${formId} not found`);
    }
    const formSubmission = step.formSubmissionForLastActivation;

    if (formSubmission == null) {
      // what to do?
      throw new Error(`Form with definitionId ${formId} has no submission`);
    }

    const value: unknown[] | null = JSONPath({
      path: formFieldJsonPath,
      json: formSubmission.submission,
    });

    return value != null && value.length > 0 && value.some((v) => v != null);
  },
  'Checks whether a JSONPath in a form submission resolves to a non-null value.',
);

export const WORKFLOW_FUNCTIONS = {
  [getFunctionSignature(workflowLaunchDateTime)]: workflowLaunchDateTime,
  [getFunctionSignature(workflowLaunchPlainDate)]: workflowLaunchPlainDate,
  [getFunctionSignature(stepStatus)]: stepStatus,
  [getFunctionSignature(stepStatusLastUpdated)]: stepStatusLastUpdated,
  [getFunctionSignature(formIsSubmitted)]: formIsSubmitted,
  [getFunctionSignature(plainDateFromForm)]: plainDateFromForm,
  [getFunctionSignature(stringFromForm)]: stringFromForm,
  [getFunctionSignature(numberFromForm)]: numberFromForm,
  [getFunctionSignature(booleanFromForm)]: booleanFromForm,
  [getFunctionSignature(formValueIsDefined)]: formValueIsDefined,
  [getFunctionSignature(preallocatedEntityRef)]: preallocatedEntityRef,
} satisfies FunctionLibrary<FunctionContext>;
